"use client";

import { useState, useEffect, useMemo } from "react";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3,
  History,
  Loader2,
  Lock,
  Target,
  XCircle,
} from "lucide-react";
import { hasFeatureAccess } from "@/lib/pass-utils";
import { PASS_FEATURES } from "@/types/pass";
import PreviousSessionDialog from "@/components/PreviousSessionDialog";
import PastExamCompletionCard from "./PastExamCompletionCard";
import StudyPlanOnTrackCard from "./StudyPlanOnTrackCard";
import SubjectProgressView from "./SubjectProgressView";
import { usePastExamCompletion } from "@/hooks/usePastExamCompletion";
import { getPassInfoFromMetadata } from "./helpers";

type StudyDataShape = {
  attendanceSummary: {
    theory: { attendedCount: number; totalPages: number };
    coding: { attendedCount: number; totalPages: number };
  };
  theory: Array<{
    subject: string;
    subjectDisplayName: string;
    attendedPages: Array<{
      slug: string;
      attendedAt: string;
      reviewCount: number;
    }>;
    attendedCount: number;
    totalPages: number;
  }>;
  coding: Array<{
    subject: string;
    subjectDisplayName: string;
    attendedPages: Array<{
      slug: string;
      attendedAt: string;
      reviewCount: number;
    }>;
    attendedCount: number;
    totalPages: number;
  }>;
  problems: {
    total: {
      totalCount: number;
      attempted: number;
      correct: number;
      incorrect: number;
      accuracyRate: number;
      attemptRate: number;
    };
    bySubject: Array<{
      subject: string;
      subjectDisplayName: string;
      scopes: Array<{
        scope: string;
        scopeDisplayName: string;
        totalCount: number;
        attempted: number;
        correct: number;
        incorrect: number;
        accuracyRate: number;
        attemptRate: number;
      }>;
      totalCount: number;
      attempted: number;
      correct: number;
      incorrect: number;
      accuracyRate: number;
      attemptRate: number;
    }>;
  };
};

export type StudyProgressTab = "overview" | "theory" | "coding" | "problems";

/**
 * @param activeTab/onTabChange 탭 전환을 우측 사이드바(StudyProgressAside)로 이전하기 위한
 *   제어형(controlled) props(JCG-2126). 라우트가 탭 상태를 소유해 aside·본문이 공유한다.
 *   미지정 시 내부 state 로 폴백(독립 사용·테스트 호환).
 */
export default function StudyProgressPage({
  activeTab: controlledTab,
  onTabChange,
}: {
  activeTab?: StudyProgressTab;
  onTabChange?: (t: StudyProgressTab) => void;
} = {}) {
  const { user } = useUser();
  const passInfo = getPassInfoFromMetadata(user?.publicMetadata);
  const starterPurchasedAt =
    (user?.publicMetadata?.starterPurchasedAt as string | null) ?? null;
  const hasStudyProgressAccess = hasFeatureAccess(
    passInfo.passType,
    PASS_FEATURES.studyProgress,
    starterPurchasedAt
  );

  // 기출 회차 완주 진행상황(JCG-2263). 프리미엄 이상 + 로드 완료 시에만 showCompletion=true.
  const {
    showCompletion,
    completionByRound,
    completedRoundCount,
    totalRoundCount,
    allRounds,
  } = usePastExamCompletion();

  const [studyData, setStudyData] = useState<StudyDataShape | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [problemBreadcrumb, setProblemBreadcrumb] = useState<{
    category: "all" | "theory" | "coding";
    subject: string | null;
  }>({ category: "all", subject: null });
  // 제어형(props) ↔ 비제어형(내부 state) 양립 — controlledTab 이 주어지면 그것을 단일 소스로 쓴다.
  const [internalTab, setInternalTab] = useState<StudyProgressTab>("overview");
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (t: StudyProgressTab) => {
    if (onTabChange) onTabChange(t);
    else setInternalTab(t);
  };

  // 카테고리별 과목 분류
  const THEORY_SUBJECT_KEYS = [
    "DB",
    "network-os",
    "sw-dev",
    "sw-design",
    "security-newtech",
  ];
  const CODING_SUBJECT_KEYS = ["c", "java", "python"];
  const CATEGORY_DISPLAY: Record<string, string> = {
    theory: "이론",
    coding: "코딩",
  };

  const getSubjectsForCategory = (category: "theory" | "coding") => {
    const keys =
      category === "theory" ? THEORY_SUBJECT_KEYS : CODING_SUBJECT_KEYS;
    return (
      studyData?.problems.bySubject.filter((s) => keys.includes(s.subject)) || []
    );
  };

  const getCategoryStats = (category: "theory" | "coding") => {
    const subjects = getSubjectsForCategory(category);
    const agg = subjects.reduce(
      (acc, s) => ({
        totalCount: acc.totalCount + s.totalCount,
        attempted: acc.attempted + s.attempted,
        correct: acc.correct + s.correct,
        incorrect: acc.incorrect + s.incorrect,
      }),
      { totalCount: 0, attempted: 0, correct: 0, incorrect: 0 }
    );
    return {
      ...agg,
      attemptRate:
        agg.totalCount > 0 ? (agg.attempted / agg.totalCount) * 100 : 0,
      accuracyRate: agg.attempted > 0 ? (agg.correct / agg.attempted) * 100 : 0,
    };
  };

  const getAccuracyToneClass = (accuracyRate: number) =>
    accuracyRate >= 70
      ? "text-green-600 dark:text-green-400"
      : accuracyRate >= 50
        ? "text-amber-600 dark:text-amber-400"
        : "text-red-600 dark:text-red-400";

  useEffect(() => {
    const fetchStudyProgress = async () => {
      if (!hasStudyProgressAccess) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch("/api/study-progress");
        const data = await response.json();
        if (data.success) {
          setStudyData(data);
        }
      } catch (error) {
        console.error("공부 진행상황 조회 오류:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStudyProgress();
  }, [hasStudyProgressAccess]);

  // 이론·코딩 전 과목의 출석 slug 합집합 — 과목별 지도/출석 뷰가 공유(추가 fetch 없음, JCG-2163).
  // 학습 경로(오솔길)는 카테고리 경계를 넘나들 수 있어(예: C언어 경로에 /coding/common 페이지 포함)
  // 과목 버킷이 아니라 전체 합집합으로 출석 여부를 판정해야 정확하다.
  const allAttended = useMemo(() => {
    const set = new Set<string>();
    if (!studyData) return set;
    for (const t of studyData.theory)
      for (const p of t.attendedPages) set.add(p.slug);
    for (const c of studyData.coding)
      for (const p of c.attendedPages) set.add(p.slug);
    return set;
  }, [studyData]);

  // slug → 회독 수 — 출석 그리드 칸/지도 노드에 "n회독"을 표기하기 위한 맵(JCG-2348).
  // allAttended 와 동일한 이론+코딩 합집합 패턴. 출석한 페이지만 키로 존재(reviewCount>=1).
  const reviewCounts = useMemo(() => {
    const map = new Map<string, number>();
    if (!studyData) return map;
    for (const t of studyData.theory)
      for (const p of t.attendedPages) map.set(p.slug, p.reviewCount);
    for (const c of studyData.coding)
      for (const p of c.attendedPages) map.set(p.slug, p.reviewCount);
    return map;
  }, [studyData]);

  return (
    <div className="px-0 py-0 md:px-2 md:py-3">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold flex items-center">
          <BarChart3 className="h-5 w-5 mr-2" />
          공부 진행상황
        </h2>
      </div>

      {/* 학습 계획 온트랙 카드 (JCG-2310) — 전체(overview) 탭에서만 표시. Premium 잠금 분기
          "바깥"에 두어, Starter+(isAdFree) 면 카드 내부에서 스스로 노출한다(Premium 미만이라
          진도 데이터가 잠겨 있어도 온트랙 상태는 볼 수 있다). isAdFree 미만이면 카드가 null. */}
      {activeTab === "overview" && (
        <div className="mb-6">
          <StudyPlanOnTrackCard />
        </div>
      )}

      {/* 프리미엄 미만 사용자: 잠금 표시 */}
      {!hasStudyProgressAccess && (
        <Card className="shadow-xl border-2">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Lock className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-xl font-bold mb-2 dark:text-white">
              공부 진행상황 잠금
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              공부 진행상황 기능은 Premium 이상
              <br />
              이용권에서 사용 가능합니다.
            </p>
            <Button asChild size="lg" className="w-full max-w-xs">
              <Link href="/membership">이용권 업그레이드하기</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* 프리미엄 이상 사용자: 진행상황 표시 */}
      {hasStudyProgressAccess && (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : studyData ? (
            <div className="space-y-6">
              {/* 탭 네비게이션 — 제어형(우측 사이드바에서 전환, JCG-2126)일 때는 본문에서 숨기고,
                  비제어형(독립 사용)일 때만 본문 상단에 노출한다. */}
              {!controlledTab && (
                <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
                  <Button
                    variant={activeTab === "overview" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveTab("overview")}
                    className="rounded-b-none"
                  >
                    전체
                  </Button>
                  <Button
                    variant={activeTab === "theory" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveTab("theory")}
                    className="rounded-b-none"
                  >
                    이론
                  </Button>
                  <Button
                    variant={activeTab === "coding" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveTab("coding")}
                    className="rounded-b-none"
                  >
                    코딩
                  </Button>
                  <Button
                    variant={activeTab === "problems" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setActiveTab("problems")}
                    className="rounded-b-none"
                  >
                    문제
                  </Button>
                </div>
              )}

              {/* 전체 탭 */}
              {activeTab === "overview" && (
                <div className="space-y-4">
                  {!controlledTab && user?.id && (
                    <div className="flex justify-end">
                      <PreviousSessionDialog
                        mode="stats"
                        statsView="overview"
                        trigger={
                          <Button variant="outline" size="sm">
                            <History className="h-3.5 w-3.5 mr-1" />
                            이전 회차 보기
                          </Button>
                        }
                      />
                    </div>
                  )}
                  {/* 전체 요약 */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Target className="h-4 w-4" />
                        전체 현황
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* 이론 출석 요약 */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            이론 출석
                          </span>
                          <span className="text-sm font-medium">
                            {studyData.attendanceSummary.theory.attendedCount} /{" "}
                            {studyData.attendanceSummary.theory.totalPages} 페이지
                          </span>
                        </div>
                        <Progress
                          value={
                            studyData.attendanceSummary.theory.totalPages > 0
                              ? (studyData.attendanceSummary.theory.attendedCount /
                                  studyData.attendanceSummary.theory.totalPages) *
                                100
                              : 0
                          }
                          className="h-2"
                        />
                      </div>

                      {/* 코딩 출석 요약 */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            코딩 출석
                          </span>
                          <span className="text-sm font-medium">
                            {studyData.attendanceSummary.coding.attendedCount} /{" "}
                            {studyData.attendanceSummary.coding.totalPages} 페이지
                          </span>
                        </div>
                        <Progress
                          value={
                            studyData.attendanceSummary.coding.totalPages > 0
                              ? (studyData.attendanceSummary.coding.attendedCount /
                                  studyData.attendanceSummary.coding.totalPages) *
                                100
                              : 0
                          }
                          className="h-2"
                        />
                      </div>

                      {/* 문제 시도율 (전체 문제 대비) */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            문제 시도
                          </span>
                          <span className="text-sm font-medium">
                            {studyData.problems.total.attempted} /{" "}
                            {studyData.problems.total.totalCount}문제
                            <span className="text-gray-400 ml-1">
                              ({studyData.problems.total.attemptRate.toFixed(0)}
                              %)
                            </span>
                          </span>
                        </div>
                        <Progress
                          value={studyData.problems.total.attemptRate}
                          className="h-2"
                        />
                      </div>

                      {/* 문제 정답률 */}
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            시도 중 정답률
                          </span>
                          <span
                            className={`text-sm font-medium ${getAccuracyToneClass(studyData.problems.total.accuracyRate)}`}
                          >
                            {studyData.problems.total.attempted > 0
                              ? studyData.problems.total.accuracyRate.toFixed(1)
                              : 0}
                            %
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 pt-2 text-center text-xs">
                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2">
                          <p className="text-blue-600 dark:text-blue-300">시도</p>
                          <p className="font-semibold text-lg text-blue-600 dark:text-blue-300">
                            {studyData.problems.total.attempted}
                          </p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                          <p className="text-green-600 dark:text-green-300">정답</p>
                          <p className="font-semibold text-lg text-green-600 dark:text-green-300">
                            {studyData.problems.total.correct}
                          </p>
                        </div>
                        <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                          <p className="text-red-600 dark:text-red-300">오답</p>
                          <p className="font-semibold text-lg text-red-600 dark:text-red-300">
                            {studyData.problems.total.incorrect}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* 기출 회차 완주 현황 (JCG-2263) — 전체 탭은 진행바 요약만(detailed 미지정) */}
                  {showCompletion && totalRoundCount > 0 && (
                    <PastExamCompletionCard
                      completedRoundCount={completedRoundCount}
                      totalRoundCount={totalRoundCount}
                      completionByRound={completionByRound}
                      allRounds={allRounds}
                    />
                  )}
                </div>
              )}

              {/* 이론 탭 */}
              {activeTab === "theory" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      각 이론 페이지의 출석체크 버튼을 누르면 출석으로
                      인정됩니다.
                    </p>
                    {!controlledTab && user?.id && (
                      <PreviousSessionDialog
                        mode="attendance"
                        userId={user.id}
                        trigger={
                          <Button variant="outline" size="sm">
                            <History className="h-3.5 w-3.5 mr-1" />
                            이전 회차 출석 보기
                          </Button>
                        }
                      />
                    )}
                  </div>
                  <SubjectProgressView
                    category="theory"
                    subjects={studyData.theory}
                    attendedSlugs={allAttended}
                    reviewCounts={reviewCounts}
                  />
                </div>
              )}

              {/* 코딩 탭 */}
              {activeTab === "coding" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      각 코딩 페이지의 출석체크 버튼을 누르면 출석으로
                      인정됩니다.
                    </p>
                    {!controlledTab && user?.id && (
                      <PreviousSessionDialog
                        mode="attendance"
                        userId={user.id}
                        trigger={
                          <Button variant="outline" size="sm">
                            <History className="h-3.5 w-3.5 mr-1" />
                            이전 회차 출석 보기
                          </Button>
                        }
                      />
                    )}
                  </div>
                  <SubjectProgressView
                    category="coding"
                    subjects={studyData.coding}
                    attendedSlugs={allAttended}
                    reviewCounts={reviewCounts}
                  />
                </div>
              )}

              {/* 문제 탭 */}
              {activeTab === "problems" && (
                <div className="space-y-4">
                  {!controlledTab && user?.id && (
                    <div className="flex justify-end">
                      <PreviousSessionDialog
                        mode="stats"
                        statsView="problems"
                        trigger={
                          <Button variant="outline" size="sm">
                            <History className="h-3.5 w-3.5 mr-1" />
                            이전 회차 보기
                          </Button>
                        }
                      />
                    </div>
                  )}

                  {/* 기출 회차 완주 현황 (JCG-2263) — 문제 탭은 전체 회차 박스 그리드까지 상세 표시 */}
                  {showCompletion && totalRoundCount > 0 && (
                    <PastExamCompletionCard
                      completedRoundCount={completedRoundCount}
                      totalRoundCount={totalRoundCount}
                      completionByRound={completionByRound}
                      allRounds={allRounds}
                      detailed
                    />
                  )}

                  {/* 계층 필터 */}
                  <div className="space-y-2">
                    {/* 1단계: 전체 / 이론 / 코딩 */}
                    <div className="flex gap-1.5 flex-wrap">
                      <button
                        type="button"
                        onClick={() =>
                          setProblemBreadcrumb({
                            category: "all",
                            subject: null,
                          })
                        }
                        className={`min-h-8 rounded-full px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                          problemBreadcrumb.category === "all"
                            ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                        }`}
                      >
                        전체
                      </button>
                      {(["theory", "coding"] as const).map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() =>
                            setProblemBreadcrumb({ category: cat, subject: null })
                          }
                          className={`min-h-8 rounded-full px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                            problemBreadcrumb.category === cat
                              ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                          }`}
                        >
                          {CATEGORY_DISPLAY[cat]}
                        </button>
                      ))}
                    </div>

                    {/* 2단계: 하위 과목 (카테고리 선택 시) */}
                    {problemBreadcrumb.category !== "all" && (
                      <div className="flex gap-1.5 flex-wrap">
                        <button
                          type="button"
                          onClick={() =>
                            setProblemBreadcrumb({
                              category: problemBreadcrumb.category,
                              subject: null,
                            })
                          }
                          className={`min-h-8 rounded-full px-2.5 py-0.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                            !problemBreadcrumb.subject
                              ? "bg-blue-600 text-white"
                              : "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                          }`}
                        >
                          전체
                        </button>
                        {getSubjectsForCategory(
                          problemBreadcrumb.category
                        ).map((subj) => (
                          <button
                            key={subj.subject}
                            type="button"
                            onClick={() =>
                              setProblemBreadcrumb({
                                category: problemBreadcrumb.category,
                                subject: subj.subject,
                              })
                            }
                            className={`min-h-8 rounded-full px-2.5 py-0.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                              problemBreadcrumb.subject === subj.subject
                                ? "bg-blue-600 text-white"
                                : "bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                            }`}
                          >
                            {subj.subjectDisplayName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 전체 보기: 계층적 리스트 */}
                  {problemBreadcrumb.category === "all" && (
                    <Card>
                      <CardContent className="py-4 space-y-3">
                        {/* 전체 요약 */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-semibold text-sm">전체</span>
                            <span className="text-xs text-gray-500">
                              정답률{" "}
                              <span
                                className={`font-semibold ${getAccuracyToneClass(studyData.problems.total.accuracyRate)}`}
                              >
                                {studyData.problems.total.attempted > 0
                                  ? studyData.problems.total.accuracyRate.toFixed(
                                      0
                                    )
                                  : 0}
                                %
                              </span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <Progress
                                value={studyData.problems.total.attemptRate}
                                className="h-2"
                              />
                            </div>
                            <span className="text-xs font-medium text-blue-600 w-14 text-right dark:text-blue-300">
                              {studyData.problems.total.attempted}/
                              {studyData.problems.total.totalCount}
                            </span>
                          </div>
                        </div>

                        <div className="border-t dark:border-gray-700" />

                        {/* 카테고리별 + 과목별 */}
                        {(["theory", "coding"] as const).map((cat) => {
                          const catStats = getCategoryStats(cat);
                          const subjects = getSubjectsForCategory(cat);
                          return (
                            <div key={cat}>
                              {/* 카테고리 행 */}
                              <button
                                type="button"
                                onClick={() =>
                                  setProblemBreadcrumb({
                                    category: cat,
                                    subject: null,
                                  })
                                }
                                className="group w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                              >
                                <div className="flex justify-between items-center mb-1">
                                  <span className="font-medium text-sm group-hover:text-blue-600 transition-colors">
                                    {CATEGORY_DISPLAY[cat]}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    정답률{" "}
                                    <span
                                      className={`font-semibold ${getAccuracyToneClass(catStats.accuracyRate)}`}
                                    >
                                      {catStats.attempted > 0
                                        ? catStats.accuracyRate.toFixed(0)
                                        : 0}
                                      %
                                    </span>
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex-1">
                                    <Progress
                                      value={catStats.attemptRate}
                                      className="h-1.5"
                                    />
                                  </div>
                                  <span className="text-xs font-medium text-blue-600 w-14 text-right dark:text-blue-300">
                                    {catStats.attempted}/{catStats.totalCount}
                                  </span>
                                </div>
                              </button>

                              {/* 과목 행 (들여쓰기) */}
                              <div className="mt-2 ml-3 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-2">
                                {subjects.map((subj) => (
                                  <button
                                    key={subj.subject}
                                    type="button"
                                    onClick={() =>
                                      setProblemBreadcrumb({
                                        category: cat,
                                        subject: subj.subject,
                                      })
                                    }
                                    className="group w-full rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                                  >
                                    <div className="flex justify-between items-center mb-0.5">
                                      <span className="text-xs text-gray-600 dark:text-gray-400 group-hover:text-blue-600 transition-colors">
                                        {subj.subjectDisplayName}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        <span
                                          className={getAccuracyToneClass(subj.accuracyRate)}
                                        >
                                          {subj.attempted > 0
                                            ? subj.accuracyRate.toFixed(0)
                                            : 0}
                                          %
                                        </span>
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="flex-1">
                                        <Progress
                                          value={subj.attemptRate}
                                          className="h-1"
                                        />
                                      </div>
                                      <span className="text-xs text-gray-500 w-14 text-right">
                                        {subj.attempted}/{subj.totalCount}
                                      </span>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  )}

                  {/* 카테고리 필터 뷰 */}
                  {problemBreadcrumb.category !== "all" &&
                    !problemBreadcrumb.subject &&
                    (() => {
                      const subjects = getSubjectsForCategory(
                        problemBreadcrumb.category
                      );
                      const catStats = getCategoryStats(
                        problemBreadcrumb.category
                      );
                      return (
                        <>
                          {/* 카테고리 합산 */}
                          <Card>
                            <CardContent className="py-4">
                              <div className="flex justify-between items-center mb-2">
                                <span className="font-medium">
                                  {CATEGORY_DISPLAY[problemBreadcrumb.category]}
                                </span>
                                <span className="font-semibold text-blue-600 dark:text-blue-300">
                                  {catStats.attempted} / {catStats.totalCount}
                                </span>
                              </div>
                              <Progress
                                value={catStats.attemptRate}
                                className="h-2 mb-2"
                              />
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                <span className="text-blue-600 dark:text-blue-300">
                                  시도 {catStats.attempted}
                                </span>
                                <span className="text-green-600 dark:text-green-300">
                                  정답 {catStats.correct}
                                </span>
                                <span className="text-red-600 dark:text-red-300">
                                  오답 {catStats.incorrect}
                                </span>
                                <span
                                  className={getAccuracyToneClass(catStats.accuracyRate)}
                                >
                                  정답률{" "}
                                  {catStats.attempted > 0
                                    ? catStats.accuracyRate.toFixed(0)
                                    : 0}
                                  %
                                </span>
                              </div>
                            </CardContent>
                          </Card>

                          {/* 과목별 카드 (scope 포함) */}
                          {subjects.map((subject) => (
                            <Card key={subject.subject}>
                              <CardContent className="py-4">
                                <div className="flex justify-between items-center mb-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setProblemBreadcrumb({
                                        ...problemBreadcrumb,
                                        subject: subject.subject,
                                      })
                                    }
                                    className="rounded-sm font-medium transition-colors hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                                  >
                                    {subject.subjectDisplayName}
                                  </button>
                                  <span className="font-semibold text-blue-600 dark:text-blue-300">
                                    {subject.attempted} / {subject.totalCount}
                                  </span>
                                </div>
                                <Progress
                                  value={subject.attemptRate}
                                  className="h-2 mb-2"
                                />
                                <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                  <span className="text-blue-600 dark:text-blue-300">
                                    시도 {subject.attempted}
                                  </span>
                                  <span className="text-green-600 dark:text-green-300">
                                    정답 {subject.correct}
                                  </span>
                                  <span className="text-red-600 dark:text-red-300">
                                    오답 {subject.incorrect}
                                  </span>
                                  <span
                                    className={getAccuracyToneClass(subject.accuracyRate)}
                                  >
                                    정답률{" "}
                                    {subject.attempted > 0
                                      ? subject.accuracyRate.toFixed(0)
                                      : 0}
                                    %
                                  </span>
                                </div>
                                {subject.scopes.length > 0 && (
                                  <div className="border-t pt-2 space-y-2">
                                    {subject.scopes.map((scope) => (
                                      <div
                                        key={scope.scope}
                                        className="flex items-center gap-2"
                                      >
                                        <span className="text-xs text-gray-500 w-24 truncate">
                                          {scope.scopeDisplayName}
                                        </span>
                                        <div className="flex-1">
                                          <Progress
                                            value={scope.attemptRate}
                                            className="h-1.5"
                                          />
                                        </div>
                                        <span className="text-xs font-medium w-16 text-right text-blue-600 dark:text-blue-300">
                                          {scope.attempted}/{scope.totalCount}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </>
                      );
                    })()}

                  {/* 과목 필터 뷰 (scope 상세) */}
                  {problemBreadcrumb.subject &&
                    (() => {
                      const subject = studyData.problems.bySubject.find(
                        (s) => s.subject === problemBreadcrumb.subject
                      );
                      if (!subject) return null;
                      return (
                        <Card>
                          <CardContent className="py-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-medium">
                                {subject.subjectDisplayName}
                              </span>
                              <span className="font-semibold text-blue-600 dark:text-blue-300">
                                {subject.attempted} / {subject.totalCount}
                              </span>
                            </div>
                            <Progress
                              value={subject.attemptRate}
                              className="h-2 mb-2"
                            />
                            <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              <span className="text-blue-600 dark:text-blue-300">
                                시도 {subject.attempted}
                              </span>
                              <span className="text-green-600 dark:text-green-300">
                                정답 {subject.correct}
                              </span>
                              <span className="text-red-600 dark:text-red-300">
                                오답 {subject.incorrect}
                              </span>
                              <span
                                className={getAccuracyToneClass(subject.accuracyRate)}
                              >
                                정답률{" "}
                                {subject.attempted > 0
                                  ? subject.accuracyRate.toFixed(0)
                                  : 0}
                                %
                              </span>
                            </div>
                            {subject.scopes.length > 0 && (
                              <div className="border-t pt-2 space-y-2">
                                {subject.scopes.map((scope) => (
                                  <div
                                    key={scope.scope}
                                    className="flex items-center gap-2"
                                  >
                                    <span className="text-xs text-gray-500 w-24 truncate">
                                      {scope.scopeDisplayName}
                                    </span>
                                    <div className="flex-1">
                                      <Progress
                                        value={scope.attemptRate}
                                        className="h-1.5"
                                      />
                                    </div>
                                    <span className="text-xs font-medium w-16 text-right text-blue-600 dark:text-blue-300">
                                      {scope.attempted}/{scope.totalCount}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })()}

                  {studyData.problems.total.attempted === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <XCircle className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>아직 풀이 기록이 없습니다.</p>
                      <p className="text-sm">문제를 풀어보세요!</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              데이터를 불러올 수 없습니다.
            </div>
          )}
        </>
      )}
    </div>
  );
}
